/**
 * Luxon Utilities for Timezone-Aware Date Handling
 *
 * This utility file provides consistent date handling across the application
 * using Luxon library. All dates are stored as ISO strings and converted
 * to the user's timezone when needed.
 *
 * Key Principles:
 * 1. Always use user's timezone (from brand.timezone)
 * 2. Store dates as ISO strings (e.g., "2024-01-29T10:00:00")
 * 3. Never use new Date() directly - use these utilities instead
 * 4. Parse dates in user's timezone, not device timezone
 */

import { DateTime } from "luxon";

/**
 * Parse a date string or timestamp in the user's timezone
 * @param {string|number|Date} input - Date input (ISO string, timestamp, or Date object)
 * @param {string} timezone - IANA timezone (e.g., "America/Toronto")
 * @returns {DateTime} Luxon DateTime object in the user's timezone
 */
export const parseInTimezone = (input, timezone) => {
  if (!timezone) {
    console.warn("No timezone provided, using system timezone");
    timezone = DateTime.local().zoneName;
  }

  // Handle null/undefined
  if (!input) {
    return DateTime.now().setZone(timezone);
  }

  // If it's already a Luxon DateTime
  if (input instanceof DateTime) {
    return input.setZone(timezone);
  }

  // If it's a timestamp (number)
  if (typeof input === "number") {
    return DateTime.fromMillis(input, { zone: timezone });
  }

  // If it's a Date object
  // IMPORTANT: Use fromMillis, not fromJSDate
  // fromJSDate interprets the Date as local time, which causes timezone shifts
  // fromMillis correctly uses the UTC timestamp
  if (input instanceof Date) {
    return DateTime.fromMillis(input.getTime(), { zone: timezone });
  }

  // If it's an ISO string
  if (typeof input === "string") {
    // Try parsing as ISO first
    let dt = DateTime.fromISO(input, { zone: timezone });
    if (dt.isValid) return dt;

    // Try parsing as SQL format
    dt = DateTime.fromSQL(input, { zone: timezone });
    if (dt.isValid) return dt;

    // Try parsing as HTTP format
    dt = DateTime.fromHTTP(input, { zone: timezone });
    if (dt.isValid) return dt;

    console.error("Could not parse date string:", input);
    return DateTime.now().setZone(timezone);
  }

  console.error("Unsupported date input type:", typeof input);
  return DateTime.now().setZone(timezone);
};

/**
 * Get current date/time in user's timezone
 * @param {string} timezone - IANA timezone (e.g., "America/Toronto")
 * @returns {DateTime} Current DateTime in user's timezone
 */
export const nowInTimezone = (timezone) => {
  if (!timezone) {
    console.warn("No timezone provided, using system timezone");
    timezone = DateTime.local().zoneName;
  }
  return DateTime.now().setZone(timezone);
};

/**
 * Convert a date to ISO string in user's timezone
 * @param {string|number|Date|DateTime} input - Date input
 * @param {string} timezone - IANA timezone (e.g., "America/Toronto")
 * @returns {string} ISO string representation
 */
export const toISOString = (input, timezone) => {
  const dt = parseInTimezone(input, timezone);
  return dt.toISO();
};

/**
 * Convert a date to timestamp (milliseconds since epoch)
 * @param {string|number|Date|DateTime} input - Date input
 * @param {string} timezone - IANA timezone (e.g., "America/Toronto")
 * @returns {number} Timestamp in milliseconds
 */
export const toTimestamp = (input, timezone) => {
  const dt = parseInTimezone(input, timezone);
  return dt.toMillis();
};

/**
 * Format a date for display
 * @param {string|number|Date|DateTime} input - Date input
 * @param {string} timezone - IANA timezone (e.g., "America/Toronto")
 * @param {string} format - Luxon format string or preset (e.g., "DATETIME_FULL", "yyyy-MM-dd HH:mm")
 * @returns {string} Formatted date string
 */
export const formatDate = (
  input,
  timezone,
  format = DateTime.DATETIME_FULL
) => {
  const dt = parseInTimezone(input, timezone);

  // Check if format is a preset constant
  if (typeof format === "object") {
    return dt.toLocaleString(format);
  }

  // Otherwise use it as a format string
  return dt.toFormat(format);
};

/**
 * Parse a calendar date string (YYYY-MM-DD) in user's timezone at midnight
 * This is specifically for calendar cell dates that don't have time information
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @param {string} timezone - IANA timezone (e.g., "America/Toronto")
 * @returns {DateTime} DateTime at midnight in user's timezone
 */
export const parseCalendarDate = (dateString, timezone) => {
  if (!timezone) {
    console.warn("No timezone provided, using system timezone");
    timezone = DateTime.local().zoneName;
  }

  // Parse as a date in the user's timezone, not UTC
  return DateTime.fromFormat(dateString, "yyyy-MM-dd", { zone: timezone });
};

/**
 * Check if a date is in the past relative to user's timezone
 * @param {string|number|Date|DateTime} input - Date input
 * @param {string} timezone - IANA timezone (e.g., "America/Toronto")
 * @returns {boolean} True if date is in the past
 */
export const isPast = (input, timezone) => {
  const dt = parseInTimezone(input, timezone);
  const now = nowInTimezone(timezone);
  return dt < now;
};

/**
 * Check if a date is in the future relative to user's timezone
 * @param {string|number|Date|DateTime} input - Date input
 * @param {string} timezone - IANA timezone (e.g., "America/Toronto")
 * @returns {boolean} True if date is in the future
 */
export const isFuture = (input, timezone) => {
  const dt = parseInTimezone(input, timezone);
  const now = nowInTimezone(timezone);
  return dt > now;
};

/**
 * Check if two dates are on the same day in user's timezone
 * @param {string|number|Date|DateTime} date1 - First date
 * @param {string|number|Date|DateTime} date2 - Second date
 * @param {string} timezone - IANA timezone (e.g., "America/Toronto")
 * @returns {boolean} True if dates are on the same day
 */
export const isSameDay = (date1, date2, timezone) => {
  const dt1 = parseInTimezone(date1, timezone);
  const dt2 = parseInTimezone(date2, timezone);
  return dt1.hasSame(dt2, "day");
};

/**
 * Get the start of day (midnight) for a date in user's timezone
 * @param {string|number|Date|DateTime} input - Date input
 * @param {string} timezone - IANA timezone (e.g., "America/Toronto")
 * @returns {DateTime} DateTime at start of day
 */
export const startOfDay = (input, timezone) => {
  const dt = parseInTimezone(input, timezone);
  return dt.startOf("day");
};

/**
 * Get the end of day (23:59:59.999) for a date in user's timezone
 * @param {string|number|Date|DateTime} input - Date input
 * @param {string} timezone - IANA timezone (e.g., "America/Toronto")
 * @returns {DateTime} DateTime at end of day
 */
export const endOfDay = (input, timezone) => {
  const dt = parseInTimezone(input, timezone);
  return dt.endOf("day");
};

/**
 * Add duration to a date
 * @param {string|number|Date|DateTime} input - Date input
 * @param {Object} duration - Duration object (e.g., { hours: 1, minutes: 30 })
 * @param {string} timezone - IANA timezone (e.g., "America/Toronto")
 * @returns {DateTime} New DateTime with duration added
 */
export const addDuration = (input, duration, timezone) => {
  const dt = parseInTimezone(input, timezone);
  return dt.plus(duration);
};

/**
 * Subtract duration from a date
 * @param {string|number|Date|DateTime} input - Date input
 * @param {Object} duration - Duration object (e.g., { hours: 1, minutes: 30 })
 * @param {string} timezone - IANA timezone (e.g., "America/Toronto")
 * @returns {DateTime} New DateTime with duration subtracted
 */
export const subtractDuration = (input, duration, timezone) => {
  const dt = parseInTimezone(input, timezone);
  return dt.minus(duration);
};

/**
 * Convert Luxon DateTime to JavaScript Date object
 * WARNING: This loses timezone information! Only use when absolutely necessary
 * (e.g., for legacy libraries that require Date objects like react-datetime)
 *
 * IMPORTANT: When converting back from JS Date to DateTime, you MUST use:
 *   DateTime.fromMillis(jsDate.getTime(), { zone: timezone })
 * NOT:
 *   DateTime.fromJSDate(jsDate, { zone: timezone })
 * Because fromJSDate interprets the Date as local time, causing timezone shifts!
 *
 * @param {DateTime} dateTime - Luxon DateTime object
 * @returns {Date} JavaScript Date object
 */
export const toJSDate = (dateTime) => {
  if (!(dateTime instanceof DateTime)) {
    console.warn("toJSDate expects a Luxon DateTime object");
    return new Date();
  }
  return dateTime.toJSDate();
};

/**
 * Compare two dates
 * @param {string|number|Date|DateTime} date1 - First date
 * @param {string|number|Date|DateTime} date2 - Second date
 * @param {string} timezone - IANA timezone (e.g., "America/Toronto")
 * @returns {number} -1 if date1 < date2, 0 if equal, 1 if date1 > date2
 */
export const compareDates = (date1, date2, timezone) => {
  const dt1 = parseInTimezone(date1, timezone);
  const dt2 = parseInTimezone(date2, timezone);

  if (dt1 < dt2) return -1;
  if (dt1 > dt2) return 1;
  return 0;
};

/**
 * Validate if a date string/timestamp is valid
 * @param {string|number} input - Date input to validate
 * @returns {boolean} True if valid
 */
export const isValidDate = (input) => {
  if (typeof input === "number") {
    const dt = DateTime.fromMillis(input);
    return dt.isValid;
  }

  if (typeof input === "string") {
    let dt = DateTime.fromISO(input);
    if (dt.isValid) return true;

    dt = DateTime.fromSQL(input);
    if (dt.isValid) return true;

    dt = DateTime.fromHTTP(input);
    if (dt.isValid) return true;
  }

  return false;
};

/**
 * Get timezone offset in hours for display
 * @param {string} timezone - IANA timezone (e.g., "America/Toronto")
 * @returns {string} Formatted offset (e.g., "GMT-5")
 */
export const getTimezoneOffset = (timezone) => {
  const dt = DateTime.now().setZone(timezone);
  const offset = dt.offset / 60; // Convert minutes to hours
  const sign = offset >= 0 ? "+" : "";
  return `GMT${sign}${offset}`;
};

/**
 * Get date in YYYY-MM-DD format in user's timezone
 * This replaces the old getISODate() which incorrectly used UTC
 * @param {Date|string|number|DateTime} input - Date input
 * @param {string} timezone - IANA timezone (e.g., "America/Toronto")
 * @returns {string} Date in YYYY-MM-DD format
 */
export const getISODateInTimezone = (input, timezone) => {
  const dt = parseInTimezone(input, timezone);
  return dt.toFormat("yyyy-MM-dd");
};

export const toMillis = (input) => {
  const dt = parseInTimezone(input);
  return dt.toMillis();
};
